import { useQuery } from '@apollo/client';
import Button from '@atlaskit/button';
import {
  AppContent,
  AppPage,
  CopyableText,
  ErrorComponents,
  FormattedAddress,
  OpenSidebarButton,
  PageHeader,
  PageTabs,
  PaymentErrorLozenge,
  PricingEligibility,
  PricingPlanLozenge,
  SummaryGridItem,
  SummarySection,
  TaxExempt,
  InactiveLozenge,
  Breadcrumbs,
  TaxId,
  useFeatureToggle,
  useTitle,
  useRecentlyViewed,
} from '@css-xp/base';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';
// @ts-ignore
import TransactionAccountIcon from '@css-xp/base/assets/img/icon-credits.svg';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { SetPaymentMethod } from '@css-xp/base/common-sidebars';
import { ProductTabTxA } from '@css-xp/base/common-tabs';
import {
  FetchTransactionAccountQuery,
  FetchTransactionAccountQueryVariables,
  PaymentMethodType,
  TransactionAccount,
} from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';
import isEmpty from 'lodash/isEmpty';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import React from 'react';
import { useParams } from 'react-router-dom';
import BillingContactTab from '../../tabs/billing-contact-tab/src/billing-contact-tab';
import { InvoiceGroupsTab } from '../../tabs/invoice-group-tabs';
import InvoiceTabTxA from '../../tabs/invoice-group-tabs/src/invoice-tab-transaction-account';
import TransactionAccountDetailsTab from './component/transaction-account-details-tab';
import { fetchTransactionAccount } from './queries/fetch-transaction-account-page';
import { AddPaymentOnTermsMethod } from './sidebar-panels/add-payment-on-terms-method-sidebar';
import { CreateInvoiceGroupSidebar } from './sidebar-panels/create-invoice-group-sidebar';
import { CreateTransactionAccountSidebar } from './sidebar-panels/create-transaction-account-sidebar';
import { EditBillingDetails, EditBillingDetailsSidebar } from './sidebar-panels/edit-billing-details-sidebar';
import { EditShipToPartySidebar } from './sidebar-panels/edit-ship-to-party';
import SetPricingEligibilitySidebar from './sidebar-panels/set-pricing-eligibility';
import { TransactionAccountActions } from './transaction-account-actions';
import './transaction-account-page.scss';
import Summary from './component/transaction-account-summary';

const getTransactionAccount = (queryData: FetchTransactionAccountQuery) => {
  return queryData?.transactionAccount as TransactionAccount;
};

export enum SIDEBAR {
  EDIT_TRANSACTION_ACCOUNT = 'editTransactionAccount',
  CREATE_TRANSACTION_ACCOUNT = 'createTransactionAccount',
  EDIT_SHIP_TO_PARTY = 'editShipToParty',
  SET_PRICING_ELIGIBILITY = 'editBillToParty',
  CREATE_INVOICE_GROUP = 'createInvoiceGroup',
  SET_CARD_PAYMENT_DEFAULT = 'setCardPaymentDefault',
  ADD_PAYMENT_ON_TERMS_METHOD = 'addPaymentOnTermsMethod',
}

// TODO: Need to use featureFlag once that is available
const isInvoiceGroupsEnabled = true;

const isTaxExemptFlagEnabled = true;

const IS_MOCK = false;

interface TransactionAccountPageProps {
  title?: string;
}

export const TransactionAccountPage = ({ title }: TransactionAccountPageProps) => {
  const { id } = useParams<{ id: string }>();
  const allowedSidebars = {
    [SIDEBAR.EDIT_TRANSACTION_ACCOUNT]: EditBillingDetailsSidebar,
    [SIDEBAR.CREATE_TRANSACTION_ACCOUNT]: CreateTransactionAccountSidebar,
    [SIDEBAR.SET_PRICING_ELIGIBILITY]: SetPricingEligibilitySidebar,
    [SIDEBAR.CREATE_INVOICE_GROUP]: CreateInvoiceGroupSidebar,
    [SIDEBAR.EDIT_SHIP_TO_PARTY]: EditShipToPartySidebar,
    [SIDEBAR.SET_CARD_PAYMENT_DEFAULT]: SetPaymentMethod,
    [SIDEBAR.ADD_PAYMENT_ON_TERMS_METHOD]: AddPaymentOnTermsMethod,
  };
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(id, title);
  }
  const transactionAccountPageResult = useQuery<FetchTransactionAccountQuery, FetchTransactionAccountQueryVariables>(
    fetchTransactionAccount,
    {
      variables: { id: id ?? '' },
      fetchPolicy: 'no-cache',
      skip: !id,
    }
  );
  const { data, loading, error, refetch } = transactionAccountPageResult;

  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          transactionAccountNumber: data.transactionAccount.transactionAccountNumber,
          displayName: data.transactionAccount.displayName,
        },
      });
    }
  }, [data, title]);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  const transactionAccount = data?.transactionAccount;
  const transactionAccountId = transactionAccount?.id ?? id;
  const transactionAccountNumber = transactionAccount?.transactionAccountNumber;
  const displayName = transactionAccount?.displayName;
  const billToAddress = transactionAccount?.billToParty;
  const billingAdmins = transactionAccount?.billingAdmins?.nodes ?? [];
  const firstInvoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.[0];
  const availablePaymentMethods = transactionAccount?.availablePaymentMethods?.nodes ?? [];
  const shipToAddress = firstInvoiceGroup?.shipToParty;
  const isPartner = transactionAccount?.isPartner || false;
  const billToPartyPricingEligibilty = transactionAccount?.billToPartyPricingEligibilty;
  const taxExemptFlag = billToAddress?.taxExempt ? 'Yes' : 'No';
  const isActive = transactionAccount?.status === 'ACTIVE' && !isEmpty(billingAdmins);
  let isPaymentOnTermAvailable = false;
  //@ts-ignore
  const isPaymentIssue = availablePaymentMethods.some((paymentMethod) => paymentMethod?.paymentIssue === true);

  const enableTxaRevampFlag = false; // isEnabled(ENABLE_TXA_ACCOUNT_PAGE_REVAMP);
  // Hide Add Payment on Terms Button on More menu if payment method already exist
  availablePaymentMethods.forEach((method: any) => {
    if (method.type === PaymentMethodType.DEFERRED) {
      isPaymentOnTermAvailable = true;
    }
  });

  // Include all props for the form except onClose
  const editBillingDetailsFormProps: EditBillingDetails = {
    billingAdmins,
    billToAddress,
    shipToAddress,
    transactionAccountId,
    billToPartyPricingEligibilty,
    invoiceGroupId: firstInvoiceGroup?.id ?? undefined,
    onComplete: refetch,
  };

  const getSubheaderLozenges = () => (
    <>
      {transactionAccount?.isPartner && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
      {!isActive && <InactiveLozenge />}&nbsp;
      {billToPartyPricingEligibilty && <PricingPlanLozenge plan={billToPartyPricingEligibilty} />}&nbsp;
      {isPaymentIssue && <PaymentErrorLozenge />}
    </>
  );

  const renderBreadcrumbs = () => {
    if (billToAddress?.name || transactionAccount) {
      const breadcrumbTrail = [
        {
          title: (billToAddress && billToAddress.name) ?? '',
          href: window.location.pathname,
        },
        {
          title: (transactionAccount && transactionAccount.transactionAccountNumber) ?? '',
          href: window.location.pathname,
        },
      ];

      return <Breadcrumbs items={breadcrumbTrail} />;
    }
  };

  const renderHeaderItems = () => {
    return (
      <div style={{ position: 'relative' }}>
        {displayName ?? 'Transaction Account'}&nbsp;
        <span style={{ position: 'absolute', bottom: '5px' }}>{getSubheaderLozenges()}</span>
      </div>
    );
  };

  const getSubheader = () => (
    <>
      {transactionAccount?.isPartner && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
      {!isActive && <InactiveLozenge />}
      {isPaymentIssue && <PaymentErrorLozenge />}
    </>
  );

  const renderSummary = () => {
    return enableTxaRevampFlag && transactionAccount ? (
      <Summary
        transactionAccountId={transactionAccountId}
        billToAddress={billToAddress}
        billToPartyPricingEligibilty={billToPartyPricingEligibilty}
        taxExemptFlag={taxExemptFlag}
        transactionAccountNumber={transactionAccount.transactionAccountNumber}
        editBillingDetailsFormProps={editBillingDetailsFormProps}
      />
    ) : (
      <SummarySection columns="1fr 1fr" className="transaction-account-summary">
        <SummaryGridItem>
          <div>
            {/* className="bill-to-address" */}
            <label>{billToAddress && billToAddress.addressLine1 ? 'Bill to address' : 'No address information'}</label>
            <div>
              {/* className="address-details" */}
              <FormattedAddress
                addressLine1={billToAddress?.addressLine1}
                addressLine2={billToAddress?.addressLine2}
                city={billToAddress?.city}
                country={billToAddress?.country}
                postcode={billToAddress?.postcode}
                state={billToAddress?.state}
              />
            </div>
            <TaxId taxId={billToAddress?.taxId} />
          </div>
          <br />
          {isTaxExemptFlagEnabled && <TaxExempt taxExempt={taxExemptFlag} />}
        </SummaryGridItem>
        <SummaryGridItem>
          <PricingEligibility plan={billToPartyPricingEligibilty} />
        </SummaryGridItem>
      </SummarySection>
    );
  };

  const renderHeader = () => {
    if (enableTxaRevampFlag) {
      return (
        <PageHeader
          icon={
            /*// @ts-ignore */
            <img src={TransactionAccountIcon} height={'40px'} alt="TnA" />
          }
          header={renderHeaderItems()}
          actions={
            <TransactionAccountActions
              editBillingDetailsFormProps={editBillingDetailsFormProps}
              isPartner={isPartner}
              hasPaymentOnTerms={isPaymentOnTermAvailable}
              isActive={isActive}
            />
          }
        />
      );
    }
    return (
      <PageHeader
        icon={
          /*// @ts-ignore */
          <img src={TransactionAccountIcon} height={'40px'} alt="TnA" />
        }
        header={displayName ?? 'Transaction Account'}
        subheader={getSubheader()}
        actions={
          <TransactionAccountActions
            enableTxaRevampFlag={enableTxaRevampFlag}
            editBillingDetailsFormProps={editBillingDetailsFormProps}
            isPartner={isPartner}
            hasPaymentOnTerms={isPaymentOnTermAvailable}
            isActive={isActive}
          />
        }
      />
    );
  };

  return (
    <TransactionAccountContext.Provider
      value={{
        getTransactionAccount,
        ...transactionAccountPageResult,
      }}
    >
      <AppPage allowedSidebars={allowedSidebars} id="transaction-account-page">
        {enableTxaRevampFlag && renderBreadcrumbs()}
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.TRANSACTION_ACCOUNT_SCREEN,
            attributes: {
              transactionAccountNumber,
              transactionAccountId,
              id,
              entityName: title,
            },
          }}
        />
        <AppContent>
          {renderHeader()}
          {renderSummary()}
          <PageTabs>
            <TransactionAccountDetailsTab label="Account details" />
            <BillingContactTab id={id} entityName={'transactionAccount'} label="Billing admins" />
            <InvoiceGroupsTab
              label="Invoice groups"
              transactionAccountId={transactionAccountId}
              billToAddress={billToAddress}
              disabled={!isInvoiceGroupsEnabled}
              onComplete={editBillingDetailsFormProps.onComplete}
            />
            <ProductTabTxA id={transactionAccountId} label="Product list" />
            <InvoiceTabTxA id={id} label="Invoices" />
          </PageTabs>
        </AppContent>
      </AppPage>
    </TransactionAccountContext.Provider>
  );
};

export default TransactionAccountPage;
